import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  ProgressBar,
  ApplicationRow,
  ApplicationRowHeader,
  LoadingSpinner,
  PageHeader,
  NoApplicationsState,
  ErrorState,
  RowSkeleton,
  MimePanel,
  ShareSheet,
} from '../components';
import { eventService, applicationService, mimeService } from '../services';
import { useAuth, useUser } from '../context';

/**
 * EventOperationPage - Event Management Dashboard (Screen 07)
 * Design: Vital stats, application queue, and action buttons
 */
export default function EventOperationPage() {
  const navigate = useNavigate();
  const { id: eventId } = useParams();
  const { isAuthenticated } = useAuth();
  const { profile } = useUser();

  // State
  const [event, setEvent] = useState(null);
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [greenlightLoading, setGreenlightLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Invite friends state
  const [inviteSectionOpen, setInviteSectionOpen] = useState(false);
  const [invitableFriends, setInvitableFriends] = useState([]);
  const [inviteFriendsLoading, setInviteFriendsLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState({});

  // Fetch event and applications
  const fetchData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      // Fetch event and applications in parallel
      const [eventResult, applicationsResult] = await Promise.all([
        eventService.getEvent(eventId),
        applicationService.getEventApplications(eventId),
      ]);

      if (!eventResult.success) {
        throw new Error(eventResult.error || 'Failed to load event');
      }

      setEvent(eventResult.data);

      if (applicationsResult.success) {
        setApplications(applicationsResult.data || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [eventId]);

  // Initial load
  useEffect(() => {
    if (eventId) {
      fetchData();
    }
  }, [eventId, fetchData]);

  // Optional: Set up polling for real-time updates
  useEffect(() => {
    if (!eventId || isLoading) return;

    // Poll every 30 seconds for updates
    const pollInterval = setInterval(() => {
      fetchData(true);
    }, 30000);

    return () => clearInterval(pollInterval);
  }, [eventId, isLoading, fetchData]);

  // Calculate funding progress
  const calculateFundingProgress = () => {
    if (!event) return { current: 0, target: 0, percentage: 0 };

    const acceptedApplications = applications.filter(
      (app) => app.status === 'accepted' || app.status === 'pending'
    );
    const currentFunding = acceptedApplications.reduce(
      (sum, app) => sum + (app.pledgeAmountCents || 0),
      0
    );
    const targetFunding = event.fundingGoalCents || 0;

    return {
      current: currentFunding / 100, // Convert to dollars
      target: targetFunding / 100,
      percentage: targetFunding > 0 ? (currentFunding / targetFunding) * 100 : 0,
    };
  };

  // Handle accept application
  const handleAccept = async (application) => {
    setActionLoading((prev) => ({ ...prev, [application.id]: 'accept' }));

    try {
      const result = await applicationService.acceptApplication(application.id);
      if (result.success) {
        setApplications((prev) =>
          prev.map((app) =>
            app.id === application.id ? { ...app, status: 'accepted' } : app
          )
        );
      } else {
        throw new Error(result.error || 'Failed to accept application');
      }
    } catch (err) {
      console.error('Failed to accept application:', err);
      alert(err.message);
    } finally {
      setActionLoading((prev) => ({ ...prev, [application.id]: null }));
    }
  };

  // Handle decline application
  const handleDecline = async (application) => {
    setActionLoading((prev) => ({ ...prev, [application.id]: 'decline' }));

    try {
      const result = await applicationService.declineApplication(application.id);
      if (result.success) {
        setApplications((prev) =>
          prev.map((app) =>
            app.id === application.id ? { ...app, status: 'declined' } : app
          )
        );
      } else {
        throw new Error(result.error || 'Failed to decline application');
      }
    } catch (err) {
      console.error('Failed to decline application:', err);
      alert(err.message);
    } finally {
      setActionLoading((prev) => ({ ...prev, [application.id]: null }));
    }
  };

  // Handle view credentials
  const handleViewCredentials = (application) => {
    navigate(`/users/${application.guest?.userId}?applicationId=${application.id}`);
  };

  // Handle greenlight event
  const handleGreenlight = async () => {
    if (!window.confirm('Are you sure you want to greenlight this event? This will charge all accepted applicants.')) {
      return;
    }

    setGreenlightLoading(true);

    try {
      const result = await eventService.greenlightEvent(eventId);
      if (result.success) {
        setEvent((prev) => ({ ...prev, status: 'processing_greenlight' }));
        alert('Event greenlighted successfully! Payments are being processed.');
      } else {
        throw new Error(result.error || 'Failed to greenlight event');
      }
    } catch (err) {
      console.error('Failed to greenlight event:', err);
      alert(err.message);
    } finally {
      setGreenlightLoading(false);
    }
  };

  // Handle delete event
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this event? This cannot be undone.')) {
      return;
    }
    setIsDeleting(true);
    try {
      const result = await eventService.deleteEvent(eventId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete event');
      }
      navigate('/host');
    } catch (err) {
      alert(err.message || 'Failed to delete event.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Copy share link
  const handleCopyLink = async () => {
    const eventUrl = `${window.location.origin}/events/${event?.inviteCode || eventId}`;
    try {
      await navigator.clipboard.writeText(eventUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  // Fetch invitable friends
  const fetchInvitableFriends = useCallback(async () => {
    setInviteFriendsLoading(true);
    try {
      const result = await eventService.getInvitableFriends(eventId);
      if (result.success) {
        setInvitableFriends(result.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch invitable friends:', err);
    } finally {
      setInviteFriendsLoading(false);
    }
  }, [eventId]);

  // Load friends when section is expanded
  useEffect(() => {
    if (inviteSectionOpen && invitableFriends.length === 0) {
      fetchInvitableFriends();
    }
  }, [inviteSectionOpen, invitableFriends.length, fetchInvitableFriends]);

  // Handle invite friend
  const handleInviteFriend = async (userId) => {
    setInviteLoading((prev) => ({ ...prev, [userId]: true }));
    try {
      const result = await eventService.inviteFriend(eventId, userId);
      if (result.success) {
        setInvitableFriends((prev) =>
          prev.map((f) => (f.userId === userId ? { ...f, status: 'invited' } : f))
        );
      } else {
        alert(result.error || 'Failed to send invite');
      }
    } catch (err) {
      console.error('Failed to invite friend:', err);
      alert('Failed to send invite');
    } finally {
      setInviteLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800 border-gray-500',
      pledging: 'bg-blue-100 text-blue-800 border-blue-500',
      processing_greenlight: 'bg-yellow-100 text-yellow-800 border-yellow-500',
      greenlit: 'bg-green-100 text-green-800 border-green-500',
      ended: 'bg-purple-100 text-purple-800 border-purple-500',
      canceled: 'bg-red-100 text-red-800 border-red-500',
    };
    return styles[status] || styles.draft;
  };

  // Check if can greenlight
  const funding = calculateFundingProgress();
  const isFreeEvent = event?.fundingGoalCents === 0;
  const canGreenlight =
    event?.status === 'pledging' && (isFreeEvent || funding.percentage >= 100);

  // Auth check
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-mono text-xl font-bold uppercase tracking-wider text-black mb-4">
            AUTHENTICATION REQUIRED
          </h1>
          <p className="font-mono text-sm text-gray-600 mb-6">
            Please sign in to view event operations.
          </p>
          <Button onClick={() => navigate('/login')}>SIGN IN</Button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="animate-pulse border-b-2 border-gray-200 h-24" />
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="space-y-4">
            <div className="h-32 bg-gray-100 border-2 border-gray-200" />
            <div className="h-8 bg-gray-100 w-1/3" />
            <RowSkeleton />
            <RowSkeleton />
            <RowSkeleton />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <ErrorState message={error} onRetry={() => fetchData()} />
      </div>
    );
  }

  // Ownership check — only the host can view this page
  if (event && profile && event.hostId !== profile.id) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-mono text-xl font-bold uppercase tracking-wider text-black mb-4">
            ACCESS DENIED
          </h1>
          <p className="font-mono text-sm text-gray-600 mb-6">
            You do not have permission to manage this event.
          </p>
          <Button onClick={() => navigate('/guest')}>GO TO DASHBOARD</Button>
        </div>
      </div>
    );
  }

  // Filter applications by status for display
  const pendingApplications = applications.filter((app) => app.status === 'pending');
  const acceptedApplications = applications.filter((app) => app.status === 'accepted');
  const declinedApplications = applications.filter((app) => app.status === 'declined');

  return (
    <div className="min-h-screen bg-white">
      <PageHeader
        title={event?.title || 'Event Operations'}
        subtitle={event?.location}
        breadcrumbs={[
          { label: 'Dashboard', onClick: () => navigate('/host') },
          { label: 'My Events', onClick: () => navigate('/host?tab=events') },
          { label: 'Operations' },
        ]}
        actions={
          isRefreshing ? (
            <LoadingSpinner size="sm" />
          ) : (
            <Button variant="ghost" size="sm" onClick={() => fetchData(true)}>
              REFRESH
            </Button>
          )
        }
      />

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Vital Stats Section */}
        <div className="border-2 border-black p-6 mb-8">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
              <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-gray-600 mb-2">
                VITAL STATS
              </h2>
              <div className="flex items-center gap-3">
                {/* Status badge */}
                <span
                  className={`px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wider border ${getStatusBadge(
                    event?.status
                  )}`}
                >
                  {event?.status || 'DRAFT'}
                </span>

                {/* Mode badge */}
                <span className="px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wider border border-black bg-gray-100">
                  {event?.mode === 'curated' ? 'CURATED' : 'OPEN'}
                </span>
              </div>
            </div>

            {/* Share button */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShareOpen(true)}
            >
              SHARE
            </Button>
          </div>

          {/* Funding Progress */}
          {event?.fundingGoalCents === 0 ? (
            <div className="p-4 border-2 border-black bg-gray-50">
              <p className="font-mono text-sm font-bold uppercase tracking-wider text-black">
                FREE EVENT — NO FUNDING GOAL
              </p>
            </div>
          ) : (
            <ProgressBar
              label="Party Fund Progress"
              current={funding.current}
              target={funding.target}
              showAmount
              showPercentage
            />
          )}

          {/* Quick stats */}
          <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t-2 border-black">
            <div className="text-center">
              <p className="font-mono text-3xl font-bold text-black">
                {applications.length}
              </p>
              <p className="font-mono text-xs text-gray-600 uppercase tracking-wider">
                APPLICATIONS
              </p>
            </div>
            <div className="text-center">
              <p className="font-mono text-3xl font-bold text-green-600">
                {acceptedApplications.length}
              </p>
              <p className="font-mono text-xs text-gray-600 uppercase tracking-wider">
                ACCEPTED
              </p>
            </div>
            <div className="text-center">
              <p className="font-mono text-3xl font-bold text-yellow-600">
                {pendingApplications.length}
              </p>
              <p className="font-mono text-xs text-gray-600 uppercase tracking-wider">
                PENDING
              </p>
            </div>
          </div>
        </div>

        {/* Greenlight Button */}
        {event?.status === 'pledging' && (
          <div className="mb-8">
            <Button
              variant="primary"
              fullWidth
              size="lg"
              onClick={handleGreenlight}
              disabled={!canGreenlight || greenlightLoading}
            >
              {greenlightLoading ? (
                <LoadingSpinner size="sm" />
              ) : canGreenlight ? (
                'GREENLIGHT EVENT'
              ) : (
                `GREENLIGHT (${Math.round(funding.percentage)}% FUNDED)`
              )}
            </Button>
            {!canGreenlight && !isFreeEvent && (
              <p className="mt-2 font-mono text-xs text-gray-600 text-center">
                Reach 100% funding goal to greenlight this event
              </p>
            )}
          </div>
        )}

        {/* Greenlighted/Completed state */}
        {(event?.status === 'greenlit' || event?.status === 'ended') && (
          <div className="mb-8 p-4 border-2 border-green-500 bg-green-50">
            <p className="font-mono text-sm text-green-800 text-center">
              {event?.status === 'greenlit'
                ? 'Event has been greenlighted! Payments are being processed.'
                : 'Event has been completed.'}
            </p>
          </div>
        )}

        {/* MIME Panel - Host controls for Make It More Epic */}
        {acceptedApplications.length > 0 && (
          <div className="mb-8">
            <MimePanel
              eventId={eventId}
              mimeService={mimeService}
              acceptedCount={acceptedApplications.length}
              eventStatus={event?.status}
            />
          </div>
        )}

        {/* House Rules (read-only) */}
        {event?.houseRules?.length > 0 && (
          <div className="mb-8 border-2 border-black p-6">
            <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-black mb-4">
              HOUSE RULES ({event.houseRules.length})
            </h2>
            <div className="space-y-2">
              {event.houseRules.map((rule, index) => (
                <div key={rule.id} className="flex items-start gap-2">
                  <span className="font-mono text-xs text-gray-400 w-6 text-right flex-shrink-0 mt-0.5">
                    {index + 1}.
                  </span>
                  <p className="font-mono text-sm text-gray-700">{rule.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Party Supplies */}
        {event?.partySupplies?.length > 0 && (
          <div className="mb-8 border-2 border-black p-6">
            <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-black mb-4">
              PARTY SUPPLIES ({event.partySupplies.filter((s) => s.isClaimed).length}/{event.partySupplies.length} CLAIMED)
            </h2>
            <div className="space-y-3">
              {event.partySupplies.map((supply, index) => (
                <div key={supply.id} className="flex items-center gap-3 p-2 border border-gray-200">
                  {supply.imageUrl && (
                    <div className="w-10 h-10 flex-shrink-0 border border-black overflow-hidden">
                      <img src={supply.imageUrl} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-sm text-black truncate">
                      {index + 1}. {supply.text}
                    </p>
                  </div>
                  {supply.isClaimed ? (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="w-6 h-6 flex-shrink-0 border border-black overflow-hidden bg-gray-100">
                        {supply.claimedByAvatarUrl ? (
                          <img src={supply.claimedByAvatarUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="flex items-center justify-center w-full h-full font-mono text-[10px] font-bold text-gray-400">
                            {supply.claimedByDisplayName?.charAt(0)?.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <span className="font-mono text-xs font-semibold text-green-600">
                        {supply.claimedByDisplayName || 'CLAIMED'}
                      </span>
                    </div>
                  ) : (
                    <span className="px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wider text-gray-400 flex-shrink-0">
                      UNCLAIMED
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* The Queue Section */}
        <div>
          <h2 className="font-mono text-lg font-bold uppercase tracking-wider text-black mb-4">
            THE QUEUE
          </h2>

          {applications.length === 0 ? (
            <NoApplicationsState type="received" />
          ) : (
            <div className="space-y-4">
              {/* Pending applications (curated mode) */}
              {event?.mode === 'curated' && pendingApplications.length > 0 && (
                <div>
                  <h3 className="font-mono text-sm font-semibold uppercase tracking-wider text-gray-600 mb-3">
                    AWAITING REVIEW ({pendingApplications.length})
                  </h3>
                  <div className="space-y-2">
                    {pendingApplications.map((application) => (
                      <ApplicationRow
                        key={application.id}
                        application={application}
                        onAccept={handleAccept}
                        onDecline={handleDecline}
                        onViewCredentials={handleViewCredentials}
                        onClick={handleViewCredentials}
                        showActions={event?.status === 'pledging'}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Accepted applications */}
              {acceptedApplications.length > 0 && (
                <div className={event?.mode === 'curated' && pendingApplications.length > 0 ? 'mt-8' : ''}>
                  <h3 className="font-mono text-sm font-semibold uppercase tracking-wider text-gray-600 mb-3">
                    ACCEPTED ({acceptedApplications.length})
                  </h3>
                  <div className="space-y-2">
                    {acceptedApplications.map((application) => (
                      <ApplicationRow
                        key={application.id}
                        application={application}
                        onClick={handleViewCredentials}
                        showActions={false}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Declined applications */}
              {declinedApplications.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-mono text-sm font-semibold uppercase tracking-wider text-gray-600 mb-3">
                    DECLINED ({declinedApplications.length})
                  </h3>
                  <div className="space-y-2 opacity-60">
                    {declinedApplications.map((application) => (
                      <ApplicationRow
                        key={application.id}
                        application={application}
                        onClick={handleViewCredentials}
                        showActions={false}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Invite Friends Section */}
        {event?.status === 'pledging' && (
          <div className="mt-8">
            <button
              type="button"
              onClick={() => setInviteSectionOpen((prev) => !prev)}
              className="w-full flex items-center justify-between font-mono text-lg font-bold uppercase tracking-wider text-black mb-4 hover:text-gray-600 transition-colors"
            >
              <span>INVITE FRIENDS</span>
              <span className="text-sm">{inviteSectionOpen ? '−' : '+'}</span>
            </button>

            {inviteSectionOpen && (
              <div className="border-2 border-black p-4">
                {inviteFriendsLoading ? (
                  <div className="flex justify-center py-6">
                    <LoadingSpinner size="sm" />
                  </div>
                ) : invitableFriends.length === 0 ? (
                  <p className="font-mono text-sm text-gray-500 text-center py-4">
                    Add friends to invite them to your events
                  </p>
                ) : (
                  <div className="space-y-2">
                    {invitableFriends.map((friend) => (
                      <div
                        key={friend.userId}
                        className="flex items-center gap-3 p-2 border border-gray-200"
                      >
                        {/* Avatar */}
                        <div className="w-10 h-10 flex-shrink-0 border-2 border-black overflow-hidden bg-gray-100">
                          {friend.avatarUrl ? (
                            <img
                              src={friend.avatarUrl}
                              alt={friend.displayName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center font-mono text-sm font-bold text-gray-400">
                              {friend.displayName?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                          )}
                        </div>

                        {/* Name + handle */}
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-sm font-semibold text-black truncate">
                            {friend.displayName}
                          </p>
                          {friend.handle && (
                            <p className="font-mono text-xs text-gray-500 truncate">
                              @{friend.handle}
                            </p>
                          )}
                        </div>

                        {/* Action button */}
                        {friend.status === 'none' ? (
                          <button
                            type="button"
                            onClick={() => handleInviteFriend(friend.userId)}
                            disabled={inviteLoading[friend.userId]}
                            className="px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wider border-2 border-black bg-black text-white hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {inviteLoading[friend.userId] ? '...' : 'INVITE'}
                          </button>
                        ) : friend.status === 'invited' ? (
                          <span className="px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wider border border-gray-300 text-gray-400">
                            INVITED
                          </span>
                        ) : (
                          <span className="px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wider border border-green-300 text-green-600">
                            APPLIED
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Bottom actions */}
        <div className="mt-12 pt-8 border-t-2 border-black flex flex-wrap gap-4">
          <Button
            variant="secondary"
            onClick={() => navigate(`/host/events/${eventId}/edit`)}
            disabled={!['draft', 'pledging', 'greenlit'].includes(event?.status)}
          >
            EDIT EVENT
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShareOpen(true)}
          >
            SHARE EVENT
          </Button>
          {event?.status === 'draft' && (
            <>
              <Button
                variant="primary"
                onClick={async () => {
                  const result = await eventService.publishEvent(eventId);
                  if (result.success) {
                    setEvent((prev) => ({ ...prev, status: 'pledging' }));
                  }
                }}
              >
                PUBLISH EVENT
              </Button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="font-mono text-sm text-red-600 uppercase tracking-wider hover:text-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
              >
                {isDeleting ? 'DELETING...' : 'DELETE EVENT'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Share Sheet Modal */}
      <ShareSheet
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        title={event?.title || ''}
        url={`${window.location.origin}/events/${event?.inviteCode || eventId}`}
        inviteCode={event?.inviteCode || eventId}
        date={event?.dateTime}
        status={event?.status}
        mode={event?.mode}
      />
    </div>
  );
}
